import { useRef, useState } from "react";
import type { DragEvent, ReactNode, JSX } from "react";

export interface ModuleIcon {
  readonly id: string;
  readonly icon: ReactNode;
  readonly label: string;
  readonly active?: boolean;
  readonly onClick: () => void;
  /** Extension icons are user-reorderable (DEC-17); pinned Core entries are not. */
  readonly reorderable?: boolean;
}

/** Where a drop would land relative to the hovered icon. */
type DropPosition = "before" | "after";

export interface LeftRailProps {
  readonly modules: readonly ModuleIcon[];
  readonly bottomModules?: readonly ModuleIcon[];
  readonly footer?: ReactNode;
  /** Fired when a reorderable icon is dropped, inserting it before/after the
   *  hovered reorderable icon. */
  readonly onReorder?: (dragId: string, overId: string, position: DropPosition) => void;
}

interface DragHandlers {
  readonly dragging: boolean;
  readonly indicator: DropPosition | null;
  readonly onDragStart: (e: DragEvent<HTMLButtonElement>) => void;
  readonly onDragOver: (e: DragEvent<HTMLButtonElement>) => void;
  readonly onDragLeave: (e: DragEvent<HTMLButtonElement>) => void;
  readonly onDrop: (e: DragEvent<HTMLButtonElement>) => void;
  readonly onDragEnd: (e: DragEvent<HTMLButtonElement>) => void;
}

function NavButton({
  module,
  drag,
}: {
  readonly module: ModuleIcon;
  readonly drag?: DragHandlers;
}): JSX.Element {
  return (
    <div className="relative w-full">
      {/* Drop indicator — a crisp line showing exactly where the icon lands,
          instead of the OS "+" ghost with no target (VS Code-style). */}
      {drag?.indicator === "before" && (
        <div className="pointer-events-none absolute -top-[3px] left-2 right-2 h-0.5 rounded-full bg-content" />
      )}
      {drag?.indicator === "after" && (
        <div className="pointer-events-none absolute -bottom-[3px] left-2 right-2 h-0.5 rounded-full bg-content" />
      )}
      <button
        className={`group h-12 w-full flex items-center justify-center rounded-xl border-none bg-transparent
          cursor-pointer transition-all duration-[180ms] ease-out
          outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0
          ${drag?.dragging === true ? "opacity-40" : ""}
          ${
            module.active
              ? "text-content"
              : "text-content-secondary hover:text-content"
          }`}
        onClick={module.onClick}
        title={module.label}
        aria-label={module.label}
        type="button"
        draggable={module.reorderable === true ? true : undefined}
        onDragStart={drag?.onDragStart}
        onDragOver={drag?.onDragOver}
        onDragLeave={drag?.onDragLeave}
        onDrop={drag?.onDrop}
        onDragEnd={drag?.onDragEnd}
      >
        <span
          className={`flex h-10 w-10 items-center justify-center rounded-[8px] transition-colors duration-[180ms]
            ${
              module.active ? "bg-surface-selected" : "group-hover:bg-surface-hover/60"
            }`}
        >
          {module.icon}
        </span>
      </button>
    </div>
  );
}

/**
 * Left navigation rail with module icons and labels
 */
export function LeftRail({
  modules,
  bottomModules,
  footer,
  onReorder,
}: LeftRailProps): JSX.Element {
  // Native HTML5 drag — no dnd dependency. The dragged id lives in a ref; the
  // hovered drop target (id + before/after) is React state so a crisp
  // indicator line renders where the icon will land.
  const dragId = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; position: DropPosition } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const clearDrag = (): void => {
    dragId.current = null;
    setDropTarget(null);
    setDraggingId(null);
  };

  const handlersFor = (module: ModuleIcon): DragHandlers | undefined => {
    if (onReorder === undefined) return undefined;
    return {
      dragging: draggingId === module.id,
      indicator: dropTarget?.id === module.id ? dropTarget.position : null,
      onDragStart: (e): void => {
        if (module.reorderable !== true) return;
        dragId.current = module.id;
        setDraggingId(module.id);
        // `move` (not the default `copy`) → the cursor shows a move badge,
        // never the ugly green "+".
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", module.id);
      },
      onDragOver: (e): void => {
        if (module.reorderable !== true || dragId.current === null) return;
        if (dragId.current === module.id) return; // not over itself
        e.preventDefault(); // allow drop
        e.dataTransfer.dropEffect = "move";
        // Above the vertical midpoint → insert before, else after.
        const rect = e.currentTarget.getBoundingClientRect();
        const position: DropPosition =
          e.clientY < rect.top + rect.height / 2 ? "before" : "after";
        setDropTarget((prev) =>
          prev?.id === module.id && prev.position === position
            ? prev
            : { id: module.id, position },
        );
      },
      onDragLeave: (e): void => {
        // Only clear when actually leaving this button (not entering a child).
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setDropTarget((prev) => (prev?.id === module.id ? null : prev));
      },
      onDrop: (e): void => {
        e.preventDefault();
        const from = dragId.current;
        const target = dropTarget;
        clearDrag();
        if (
          from === null ||
          module.reorderable !== true ||
          from === module.id ||
          target?.id !== module.id
        ) {
          return;
        }
        onReorder(from, module.id, target.position);
      },
      onDragEnd: (): void => {
        clearDrag();
      },
    };
  };

  return (
    <div className="flex flex-col items-center h-full w-full pt-3">
      <div className="flex flex-col items-center gap-1.5 py-2 flex-1 w-full px-1.5">
        {modules.map((module) => (
          <NavButton key={module.id} module={module} drag={handlersFor(module)} />
        ))}
      </div>
      {footer && <div className="pb-2 pt-2">{footer}</div>}
      {bottomModules && bottomModules.length > 0 && (
        <div className="flex flex-col items-center gap-1.5 pb-4 w-full px-1.5 flex-none">
          {bottomModules.map((module) => (
            <NavButton key={module.id} module={module} />
          ))}
        </div>
      )}
    </div>
  );
}
